import { ManufacturingPageFormFields } from '~/pages/ManufacturingPage';
import { ActivateRangeParams } from '~/api/manufactured_chips';
import { toServerFormat, formatDateToLongFormat } from './dateTimeUtils';
import { Organization } from '../pages/OrganizationsPage';
import {
  FilterOrganizationsParams,
  GetOrganizationsResponse,
  UpdateOrganizationParams,
} from '../api/organization';
import { User, FullUser } from '../components/UserPage';
import { Consumer } from '../pages/ConsumersPage';
import {
  GetUserResponse,
  FilterUsersParams,
  GetUsersResponse,
  UpdateUserParams,
  GetConsumersParams,
  GetConsumersResponse,
} from '../api/user';
import {
  TestResult,
  FullTestResult,
  HealthInformationAnswer,
  ErrorLog,
} from '../pages/TestListPage';
import {
  FilterTestResultsResponse,
  FilterTestResultsParams,
  GetTestResultResponse,
  isFeedbackElementAnswer,
  isFeedbackElementSection,
} from '../api/result';
import { TableFiltersData } from '../components/Table/TableFilters';

// Organizations

export const formatFilterOrganizationParams = (
  pageNumber: number,
  pageSize: number,
  orgFilters: TableFiltersData
): FilterOrganizationsParams => {
  const dataParams: FilterOrganizationsParams = {
    page_number: pageNumber,
    page_size: pageSize,
  };
  const { searchQuery, dateCreated, status } = orgFilters;
  dataParams.search_term = searchQuery || undefined;
  dataParams.date_from = dateCreated.start ? toServerFormat(dateCreated.start) : undefined;
  dataParams.date_to = dateCreated.end ? toServerFormat(dateCreated.end) : undefined;
  dataParams.status = status ? status.toLowerCase() : undefined;
  return dataParams;
};

export const formatUpdateOgranizationParams = (
  name: string,
  active: boolean,
  nhsSupport: boolean,
  email: string,
  hba1cConfig: boolean,
  initialOrganization: Organization
): UpdateOrganizationParams => {
  const orgName = name !== initialOrganization.orgName ? name : undefined;
  const organizationStatus = active ? 'active' : 'inactive';
  const status = organizationStatus !== initialOrganization.status ? organizationStatus : undefined;
  return {
    organization_id: initialOrganization.orgId,
    organization_name: orgName,
    status,
    nhs_config: {
      ...initialOrganization.nhs_config,
      nhs_support: nhsSupport,
      nhs_email_address: nhsSupport ? email : undefined,
    },
    hba1c_config: hba1cConfig,
  };
};

export const formatGetOrganizationsResponse = (
  apiResponse: GetOrganizationsResponse
): Array<Organization> => {
  const formattedOrganizations = apiResponse.organizations.map((organization) => ({
    orgId: organization.organization_id,
    orgName: organization.organization_name,
    creationTime: organization.creation_time,
    status: organization.status,
    isConsOrganization: organization.cons_organization,
    activationCode: organization.activation_code,
    orgRelation: organization.org_relation
      ? {
          orgId: organization.org_relation.org_id,
          orgName: organization.org_relation.org_name,
        }
      : undefined,
    nhs_config: organization.nhs_config,
    hba1cConfig: organization.hba1c_config,
  }));
  return formattedOrganizations;
};

// Users

export const formatFilterUserParams = (
  pageNumber: number,
  pageSize: number,
  role: 'hcp' | 'admin',
  userFilters: TableFiltersData
): FilterUsersParams => {
  const dataParams: FilterUsersParams = {
    page_number: pageNumber,
    page_size: pageSize,
    role,
  };
  const { searchQuery, dateCreated, status } = userFilters;
  dataParams.search_term = searchQuery || undefined;
  dataParams.date_from = dateCreated.start ? toServerFormat(dateCreated.start) : undefined;
  dataParams.date_to = dateCreated.end ? toServerFormat(dateCreated.end) : undefined;
  dataParams.status = status ? status.toLowerCase() : undefined;
  return dataParams;
};

export const formatUpdateUserParams = (
  orgId: string,
  name: string,
  status: 'active' | 'inactive',
  initialUser: FullUser
): UpdateUserParams => {
  const userName = name !== initialUser.name ? name : undefined;
  const userStatus = status !== initialUser.status ? status : undefined;
  const userOrgId = orgId !== initialUser.orgId ? orgId : undefined;
  return {
    user_id: initialUser.userId,
    organization_id: userOrgId,
    name: userName,
    status: userStatus,
  };
};

export const formatGetUsersResponse = (apiResponse: GetUsersResponse): Array<User> => {
  const formattedUsers: Array<User> = apiResponse.users.map((user) => ({
    userId: user.user_id,
    name: user.name,
    orgName: user.organization_name,
    orgId: user.organization_id,
    creationTime: user.creation_time,
    email: user.user_email,
    status: user.status,
    userStatus: user.user_status,
    invitationAccepted: user.invite_accepted,
  }));
  return formattedUsers;
};

export const formatGetUserResponse = (apiResponse: GetUserResponse): FullUser => ({
  userId: apiResponse.user_id,
  name: apiResponse.name,
  orgName: apiResponse.organization_name,
  orgId: apiResponse.organization_id,
  orgStatus: apiResponse.organization_status,
  creationTime: apiResponse.creation_time,
  email: apiResponse.user_email,
  status: apiResponse.status,
  enabled: apiResponse.enabled,
  userStatus: apiResponse.user_status,
  invitationAccepted: apiResponse.invite_accepted,
});

export const formatGetConsumersParams = (
  pageNumber: number,
  pageSize: number,
  consumerFilters: TableFiltersData
): GetConsumersParams => {
  const dataParams: GetConsumersParams = {
    page_number: pageNumber,
    page_size: pageSize,
  };
  const { searchQuery, dateCreated, dateLastUsed } = consumerFilters;
  dataParams.search_term = searchQuery || undefined;
  dataParams.date_from = dateCreated.start ? toServerFormat(dateCreated.start) : undefined;
  dataParams.date_to = dateCreated.end ? toServerFormat(dateCreated.end) : undefined;
  dataParams.last_used_from = dateLastUsed?.start ? toServerFormat(dateLastUsed.start) : undefined;
  dataParams.last_used_to = dateLastUsed?.end ? toServerFormat(dateLastUsed.end) : undefined;
  return dataParams;
};

export const formatGetConsumersResponse = (apiResponse: GetConsumersResponse): Array<Consumer> => {
  const formattedConsumers: Array<Consumer> = apiResponse.consumers.map((consumer) => ({
    userEmail: consumer.user_email,
    name: consumer.name,
    dateCreated: consumer.date_created,
    lastTimeUsed: consumer.last_time_used,
  }));
  return formattedConsumers;
};

// Test Results

export const formatFilterTestResultsResponse = (
  apiResponse: FilterTestResultsResponse
): Array<TestResult> => {
  const formattedTestResults: Array<TestResult> = apiResponse.results.map((result) => ({
    testId: result.test_id,
    resultId: result.result_id,
    organization: result.organization,
    testType: result.test_type,
    hcpName: result.hcp_name,
    hcpEmail: result.hcp_email,
    deviceId: result.device_id,
    lfdLot: result.lfd_lot,
    status: result.status,
    dateCreated: result.date_created,
  }));
  return formattedTestResults;
};

export const formatFilterTestResultsParams = (
  pageNumber: number,
  pageSize: number,
  testResultsFilters: TableFiltersData
): FilterTestResultsParams => {
  const dataParams: FilterTestResultsParams = {
    page_number: pageNumber,
    page_size: pageSize,
  };
  const { searchQuery, dateCreated, status } = testResultsFilters;
  dataParams.search_term = searchQuery || undefined;
  dataParams.date_from = dateCreated.start ? toServerFormat(dateCreated.start) : undefined;
  dataParams.date_to = dateCreated.end ? toServerFormat(dateCreated.end) : undefined;
  const statuses = status !== '' ? status.toLowerCase().split(', ') : [];
  dataParams.statuses = statuses.length ? statuses : undefined;
  return dataParams;
};

/**
 * returns string value of number if it exists, otherwise placeholder
 * @param numValue nullable number value
 * @param placeholder
 */

const numberToString = (numValue: number | null | undefined, placeholder: string): string => {
  return numValue ? numValue.toString() : placeholder;
};

export const formatTestResultResponse = ({
  subject_data,
  results,
  error_logs,
  nhs_email_meta,
  pkb_message_meta,
  gp_message_meta,
}: GetTestResultResponse): FullTestResult => {
  const formattedErrorLogs: Array<ErrorLog> = error_logs.map((errorLog) => ({
    errorId: errorLog.error_id,
    errorType: errorLog.error_type,
    createdAt: errorLog.created_at,
    imgCapturedAt: errorLog.img_captured_at,
    timerStartedAt: errorLog.timer_started_at,
    testSubmittedAt: errorLog.test_submitted_at,
    images: errorLog.images,
  }));
  const formattedAnswers: Array<HealthInformationAnswer> = subject_data.answers.map((answer) => ({
    label: answer.question_content,
    value: answer.answer_content,
    riskFactor: answer.risk_factor ?? 1,
    influencesBiomarkers: answer.influences_biomarkers,
  }));
  const formattedPersonalAnswers = subject_data.personal_answers.map((answer) => ({
    label: answer.question_content,
    value: answer.answer_content,
    questionTitle: answer.question_title,
  }));
  const formattedFeedbackAnswers = subject_data.feedback_answers.map((answer) => {
    if (isFeedbackElementAnswer(answer)) {
      return {
        label: answer.question_content,
        value: answer.answer_content,
        questionTitle: answer.question_title,
      };
    }
    if (isFeedbackElementSection(answer)) {
      return {
        sectionLabel: answer.section_label,
        answers: answer.answers.map((nestedAnswer) => ({
          label: nestedAnswer.question_content,
          value: nestedAnswer.answer_content,
          questionTitle: nestedAnswer.question_title,
        })),
      };
    }
    return { label: '', value: '', questionTitle: '' };
  });
  const {
    device_platform,
    device_model,
    app_ver,
    algo_ver,
    lfd_lot,
    test_started_at,
    timer_started_at,
    test_submitted_at,
    image_captured_at,
    results_created_at,
    lot_numer,
    unique_identifier,
    model_id,
  } = results.metadata;
  const formattedMetadata = [
    { label: 'OS Version', value: device_platform },
    { label: 'App Version', value: app_ver },
    { label: 'Pipeline Version', value: algo_ver || '-' },
    { label: 'Device Model', value: device_model },
    { label: 'Lot Number', value: lot_numer ?? lfd_lot ?? '-' },
    { label: 'Chip ID', value: unique_identifier ?? '-' },
    { label: 'Model Version', value: model_id ?? '-' },
    {
      label: 'Start of the Test',
      value: formatDateToLongFormat(new Date(test_started_at)),
    },
    {
      label: 'Start of the Timer',
      value: formatDateToLongFormat(new Date(timer_started_at)),
    },
    {
      label: 'Image Captured',
      value: image_captured_at ? formatDateToLongFormat(new Date(image_captured_at)) : '-',
    },
    {
      label: 'Test Submission',
      value: formatDateToLongFormat(new Date(test_submitted_at)),
    },
    {
      label: 'Test Results Created',
      value: formatDateToLongFormat(new Date(results_created_at)),
    },
  ];

  return {
    subjectData: {
      testId: subject_data.test_id,
      createdAt: subject_data.created_at,
      testStartedAt: subject_data.test_started_at,
      subjectEmail: subject_data.subject_email,
      newsAndAnnouncements: subject_data.news_and_announcements,
      pushNotificationsEnabledAtImaging: subject_data.push_notifications_enabled_at_imaging,
      bmi: subject_data.bmi,
      modelId: subject_data.model_id,
      pipelineModelRanges: subject_data.pipeline_model_ranges ?? undefined,
      ethnicity: subject_data.ethnicity,
      answers: formattedAnswers,
      personalAnswers: formattedPersonalAnswers,
      feedbackAnswers: formattedFeedbackAnswers,
      nhsNumber: subject_data.nhs_number,
      odsCode: subject_data.ods_code,
    },
    results: {
      status: results.status,
      // Chip model the pipeline actually ran (from results.metadata), distinct from
      // subject_data.model_id which defaults to "model-id-1".
      modelId: model_id ?? undefined,
      resultData: {
        l: {
          triglycerides: numberToString(results.tg_l, '--'),
          hdl: numberToString(results.hdl_l, '--'),
          totalCholesterol: numberToString(results.tc_l, '--'),
          nonHdl: '--',
          tcHdlRatio: '--',
        },
        a: {
          triglycerides: numberToString(results.tg_a, '--'),
          hdl: numberToString(results.hdl_a, '--'),
          totalCholesterol: numberToString(results.tc_a, '--'),
          nonHdl: '--',
          tcHdlRatio: '--',
        },
        b: {
          triglycerides: numberToString(results.tg_b, '--'),
          hdl: numberToString(results.hdl_b, '--'),
          totalCholesterol: numberToString(results.tc_b, '--'),
          nonHdl: '--',
          tcHdlRatio: '--',
        },
        rawValues: {
          triglycerides: numberToString(results.tg_raw, '--'),
          hdl: numberToString(results.hdl_raw, '--'),
          totalCholesterol: numberToString(results.tc_raw, '--'),
          nonHdl: '--',
          tcHdlRatio: '--',
        },
        publishedValues: {
          triglycerides: numberToString(results.triglycerides, '--'),
          hdl: numberToString(results.hdl, '--'),
          totalCholesterol: numberToString(results.total_cholesterol, '--'),
          nonHdl: numberToString(results.non_hdl, '--'),
          tcHdlRatio: numberToString(results.total_hdl_ratio, '--'),
        },
      },
      pdfUrl: results.pdf_url,
      pdfPass: results.pdf_pass,
      pdfName: results.pdf_name,
      totalCholesterol: {
        value: results.total_cholesterol,
        thresholdExceeded: results.tc_thold_exceeded,
      },
      hdl: {
        value: results.hdl,
        thresholdExceeded: results.hdl_thold_exceeded,
      },
      nonHdl: {
        value: results.non_hdl,
        thresholdExceeded: results.non_hdl_thold_exceeded,
      },
      ldl: {
        value: results.ldl,
        thresholdExceeded: results.ldl_thold_exceeded,
      },
      totalHdlRatio: {
        value: results.total_hdl_ratio,
        thresholdExceeded: results.tc_hdl_ratio_thold_exceeded,
      },
      triglycerides: {
        value: results.triglycerides,
        thresholdExceeded: results.tg_thold_exceeded,
      },
      warningMessage: results.warning_msg,
      images: {
        barcodesAndRois: results.images['barcodes&rois'],
        colorCorrected: results.images.color_corrected,
        final: results.images.final,
        inputCropped: results.images.input_cropped,
        shadeCorrected: results.images.shade_corrected,
        whiteBalanced: results.images.white_balanced,
        linearized: results.images.linearized,
        original: results.images.original,
        rois: results.images.rois,
        sitesBinaryMask: results.images.sites_binary_mask,
        levelsAdjusted: results.images.levels_adjusted,
      },
      metadata: formattedMetadata,
      qrisk: {
        qriskId: results.qrisk.qrisk_id,
        testId: results.qrisk.test_id,
        score10Year: results.qrisk.score_10_year,
        scoreHealthyPerson: results.qrisk.score_healthy_person,
        healthyHeartAge: results.qrisk.healthy_heart_age,
        status: results.qrisk.status,
        errorInfo: results.qrisk.error_info,
      },
    },
    errorLogs: formattedErrorLogs,
    nhsEmailMeta: nhs_email_meta
      ? {
          pdfName: nhs_email_meta.pdf_name,
          pdfUrl: nhs_email_meta.pdf_url,
          sentAt: nhs_email_meta.sent_at,
          status: nhs_email_meta.status,
        }
      : undefined,

    pkbMessageMeta: pkb_message_meta
      ? {
          status: pkb_message_meta.status,
          sentAt: pkb_message_meta.sent_at,
        }
      : undefined,
    gpMessageMeta: gp_message_meta
      ? {
          status: gp_message_meta.status,
          createdAt: gp_message_meta.created_at,
        }
      : undefined,
  };
};

export const formatActivateRangeParams = (
  data: ManufacturingPageFormFields
): ActivateRangeParams => {
  return {
    lot_number: data.lotNumber,
    test_type: data.product as string,
    expiry_date: toServerFormat(data.expiryDate) as string,
    model_id: data.predictionModel as string,
    defective: [], // functionality not yet supported by BE
    start_unique_id_first: data.firstLabel,
    start_unique_id_second: data.secondLabel,
    end_unique_id_first: data.secondLastLabel,
    end_unique_id_second: data.lastLabel,
  };
};
